// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getAddressFieldsDataView } from "@insite/client-framework/Store/Data/AddressFields/AddressFieldsSelector";
import { getCurrentBillToState } from "@insite/client-framework/Store/Data/BillTos/BillTosSelectors";
import updateBillTo from "@insite/client-framework/Store/Data/BillTos/Handlers/UpdateBillTo";
import { getCurrentCountries } from "@insite/client-framework/Store/Data/Countries/CountriesSelectors";
import translate from "@insite/client-framework/Translate";
import { BillToModel } from "@insite/client-framework/Types/ApiModels";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import AddressInfoDisplay from "@insite/content-library/Components/AddressInfoDisplay";
import CustomerAddressForm from "@insite/content-library/Components/CustomerAddressForm";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Modal from "@insite/mobius/Modal";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface OwnProps extends WidgetProps {}

const mapStateToProps = (state: ApplicationState) => ({
    currentBillTo: getCurrentBillToState(state).value,
    countries: getCurrentCountries(state),
    billToAddressFields: getAddressFieldsDataView(state).value?.billToAddressFields,
    customerSettings: getSettingsCollection(state).customerSettings,
});

const mapDispatchToProps = {
    updateBillTo,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface CurrentBillingAddressStyles {
    wrapper?: InjectableCss;
    titleText?: TypographyProps;
    contentText?: TypographyProps;
    editLink?: LinkPresentationProps;
    editLinkWrapper?: InjectableCss;
}

export const currentBillingAddressStyles: CurrentBillingAddressStyles = {
    wrapper: {
        css: css`
            border-top: 1px solid ${getColor("common.ui3")};
            padding-top: 15px;
        `,
    },
    titleText: {
        variant: "h5",
        as: "h5",
    },
    contentText: {
        css: css``,
    },
    editLink: {
        icon: {
            iconProps: {
                src: "Create",
                color: "secondary",
                size: 14,
            },
            position: "left",
        },
        typographyProps: {
            color: "secondary",
        },
    },
    editLinkWrapper: {
        css: css`
            display: flex !important;
            flex-direction: row !important;
            margin-top: 20px;
        `,
    },
};

const styles = currentBillingAddressStyles;

const CurrentBillingAddress: React.FunctionComponent<Props> = (props: Props) => {
    const { currentBillTo } = props;

    const [modalIsOpen, setModalIsOpen] = React.useState(false);

    if (!currentBillTo) {
        return null;
    }

    const modalCloseHandler = () => {
        setModalIsOpen(false);
    };
    const editClickHandler = () => {
        setModalIsOpen(true);
    };
    const formCancelHandler = (e: any) => {
        e.preventDefault();
        modalCloseHandler();
    };
    const formSubmitHandler = (_: React.FormEvent<HTMLFormElement>, address: BillToModel) => {
        if (!props.currentBillTo) {
            return;
        }
        props.updateBillTo({
            billTo: {
                ...props.currentBillTo,
                ...address,
            },
            isEdit: true,
        });
        modalCloseHandler();
    };

    const { countries, billToAddressFields, customerSettings } = props;
    return (
        <StyledWrapper {...styles.wrapper} data-test-selector="addressInfoDisplay_Billing">
            <Typography {...styles.titleText}>{translate("Billing Address")}</Typography>
            <AddressInfoDisplay
                firstName={currentBillTo.firstName}
                lastName={currentBillTo.lastName}
                companyName={currentBillTo.companyName}
                attention={currentBillTo.attention}
                address1={currentBillTo.address1}
                address2={currentBillTo.address2}
                address3={currentBillTo.address3}
                address4={currentBillTo.address4}
                city={currentBillTo.city}
                state={currentBillTo.state ? currentBillTo.state.abbreviation : undefined}
                postalCode={currentBillTo.postalCode}
                country={currentBillTo.country ? currentBillTo.country.abbreviation : undefined}
                phone={currentBillTo.phone}
                fax={currentBillTo.fax}
                email={currentBillTo.email}
            />

            {countries && customerSettings.allowBillToAddressEdit && (
                <StyledWrapper {...styles.editLinkWrapper}>
                    <Link {...styles.editLink} onClick={editClickHandler} data-test-selector="billTo_Edit_Address">
                        <Typography>{translate("Edit")}</Typography>
                    </Link>
                </StyledWrapper>
            )}

            {countries && billToAddressFields && (
                <Modal
                    headline={translate("Edit Billing Information")}
                    isOpen={modalIsOpen}
                    handleClose={modalCloseHandler}
                >
                    <CustomerAddressForm
                        address={currentBillTo}
                        countries={countries}
                        addressFieldDisplayCollection={billToAddressFields}
                        onCancel={formCancelHandler}
                        onSubmit={formSubmitHandler}
                    />
                </Modal>
            )}
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(CurrentBillingAddress),
    definition: {
        group: "Addresses",
        allowedContexts: ["AddressesPage"],
    },
};

export default widgetModule;
